import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getSortIndicator } from '@/lib/tableHelpers'
import { formatTrainerDistanceKm } from './projectTrainerHelpers'

const formatMoney = value => {
	const normalizedValue = `${value ?? ''}`.trim()
	return normalizedValue ? `${normalizedValue} PLN` : '—'
}

const AVAILABILITY_BADGE_CLASS = {
	available: 'bg-emerald-100 text-emerald-800',
	morning: 'bg-amber-100 text-amber-800',
	afternoon: 'bg-violet-100 text-violet-800',
	unavailable: 'bg-gray-100 text-gray-600',
	noDate: 'bg-gray-100 text-gray-500',
	unknown: 'bg-gray-100 text-gray-500',
}

const formatStepDate = value => {
	const match = `${value || ''}`.match(/^(\d{4})-(\d{2})-(\d{2})/)
	if (!match) return ''
	return `${match[3]}.${match[2]}.${match[1]}`
}

function TrainerAvailabilityBadge({ availability, t }) {
	const status = availability?.status || 'unknown'
	return (
		<span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${AVAILABILITY_BADGE_CLASS[status] || AVAILABILITY_BADGE_CLASS.unknown}`}>
			{t(`projects.details.trainerDialog.availability.${status}`, { defaultValue: '—' })}
		</span>
	)
}

export default function ProjectAddTrainerModal({
	isOpen,
	onClose,
	onSubmit,
	isSaving,
	hasMore,
	onLoadMore,
	onSearchChange,
	onSort,
	searchTerm,
	sortColumn,
	sortDirection,
	totalCount,
	trainerOptions,
	stepDate = '',
}) {
	const { t } = useTranslation()
	const rows = useMemo(
		() => (Array.isArray(trainerOptions) ? trainerOptions : []),
		[trainerOptions]
	)
	const [activeTrainerId, setActiveTrainerId] = useState('')

	useEffect(() => {
		if (!isOpen) return
		setActiveTrainerId('')
	}, [isOpen])

	const selectedTrainer = useMemo(
		() => rows.find(option => String(option.value) === String(activeTrainerId)) || null,
		[activeTrainerId, rows]
	)

	if (!isOpen) return null

	const stepDateLabel = formatStepDate(stepDate)

	const handleChoose = () => {
		if (!selectedTrainer || isSaving) return

		onSubmit({
			trainerId: selectedTrainer.value || null,
			trainerName: selectedTrainer.trainerName || '',
			miasto: selectedTrainer.miasto || '',
			g_lat: selectedTrainer.g_lat ?? '',
			g_long: selectedTrainer.g_long ?? '',
			distanceKm: selectedTrainer.distanceKm ?? null,
			certificationNames: Array.isArray(selectedTrainer.certificationNames) ? selectedTrainer.certificationNames : [],
			missingCertificationNames: Array.isArray(selectedTrainer.missingCertificationNames)
				? selectedTrainer.missingCertificationNames
				: [],
			stawki_godzinowe: selectedTrainer.stawki_godzinowe || null,
			defaultHourlyRateGross: selectedTrainer.defaultHourlyRateGross || '',
			hourlyRateGross: selectedTrainer.defaultHourlyRateGross || '',
			extraAmountGross: '',
		})
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<div className="absolute inset-0 bg-black/40" onClick={onClose} />
			<div className="relative w-full max-w-6xl rounded-lg bg-white shadow-xl">
				<div className="border-b border-gray-200 p-4">
					<h3 className="text-base font-semibold text-gray-900">{t('projects.details.trainerDialog.addTitle')}</h3>
					<p className="mt-1 text-sm text-gray-500">{t('projects.details.trainerDialog.addDescription')}</p>
				</div>

				<div className="p-4">
					<input
						type="text"
						value={searchTerm}
						onChange={event => onSearchChange(event.target.value)}
						placeholder={t('projects.details.trainerDialog.placeholders.searchTrainer')}
						className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
						autoFocus
					/>

					<div className="mt-3 max-h-72 overflow-auto rounded border border-gray-200">
						<table className="min-w-full divide-y divide-gray-200 bg-white">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-4 py-3 text-left">
										<div className="flex flex-col items-start gap-1">
											<button
												type="button"
												onClick={() => onSort('trainerName')}
												className="flex items-center text-sm font-semibold text-gray-700 hover:text-blue-600"
											>
												{t('projects.details.trainerDialog.table.trainer')}
												{getSortIndicator('trainerName', sortColumn, sortDirection)}
											</button>
											<button
												type="button"
												onClick={() => onSort('distanceKm')}
												className="flex items-center text-xs font-medium text-gray-500 hover:text-blue-600"
											>
												{t('projects.details.trainerDialog.table.distance')}
												{getSortIndicator('distanceKm', sortColumn, sortDirection)}
											</button>
										</div>
									</th>
									<th className="px-4 py-3 text-left">
										<div className="text-sm font-semibold text-gray-700">
											{t('projects.details.trainerDialog.table.availability', { defaultValue: 'Dostępność' })}
										</div>
										{stepDateLabel ? (
											<div className="text-xs font-normal text-gray-500">{stepDateLabel}</div>
										) : null}
									</th>
									<th className="px-4 py-3 text-left">
										<span className="text-sm font-semibold text-gray-700">{t('projects.details.trainerDialog.table.certifications')}</span>
									</th>
									<th className="px-4 py-3 text-left">
										<button
											type="button"
											onClick={() => onSort('defaultHourlyRateGross')}
											className="flex items-center text-sm font-semibold text-gray-700 hover:text-blue-600"
										>
											{t('projects.details.trainerDialog.table.hourlyRateGross')}
											{getSortIndicator('defaultHourlyRateGross', sortColumn, sortDirection)}
										</button>
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
								{rows.length
									? rows.map(option => {
										const distanceLabel = formatTrainerDistanceKm(option.distanceKm)

										return (
										<tr
											key={option.value}
											className={`align-top cursor-pointer ${
												String(option.value) === String(activeTrainerId) ? 'bg-blue-50' : 'hover:bg-gray-50'
											}`}
											onClick={() => setActiveTrainerId(option.value)}
										>
											<td className="px-4 py-3">
												<div className="text-sm font-medium text-gray-900">{option.trainerName || '—'}</div>
												<div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500">
													<span>{option.miasto || '—'}</span>
													{distanceLabel ? (
														<span className="text-gray-600">· {distanceLabel}</span>
													) : null}
												</div>
											</td>
											<td className="px-4 py-3">
												<TrainerAvailabilityBadge availability={option.availability} t={t} />
											</td>
											<td className="px-4 py-3">
												<div className="space-y-1">
													<div className="text-sm text-gray-700">
														{option.certificationNames?.length
															? option.certificationNames.join(', ')
															: t('projects.details.trainerDialog.emptyCertifications')}
													</div>
													{option.missingCertificationNames?.length > 0 && (
														<div className="text-xs font-medium text-amber-600">
															{t('projects.details.messages.missingCertificationsLabel', {
																list: option.missingCertificationNames.join(', '),
															})}
														</div>
													)}
												</div>
											</td>
											<td className="px-4 py-3 text-sm text-gray-700">
												{formatMoney(option.defaultHourlyRateGross)}
											</td>
										</tr>
										)
									})
									: (
										<tr>
											<td colSpan="4" className="px-4 py-6 text-center text-sm text-gray-500">
												{t('projects.details.trainerDialog.emptyOptions')}
											</td>
										</tr>
									)}
							</tbody>
						</table>
					</div>

					{activeTrainerId && (
						<p className="mt-2 text-xs text-gray-500">
							{t('projects.details.trainerDialog.selectedLabel')}
							{' '}
							<span className="font-medium text-gray-700">{selectedTrainer?.trainerName || activeTrainerId}</span>
						</p>
					)}
				</div>

				<div className="flex justify-between border-t border-gray-200 p-4">
					<div>
						{hasMore && (
							<button
								type="button"
								onClick={onLoadMore}
								className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
							>
								{t('loadMore.button')} ({rows.length}/{totalCount})
							</button>
						)}
					</div>
					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => setActiveTrainerId('')}
							className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
						>
							{t('projects.details.trainerDialog.clearSelection')}
						</button>
						<button
							type="button"
							onClick={onClose}
							className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
						>
							{t('notifications.cancelButton')}
						</button>
								<button
									type="button"
									onClick={handleChoose}
									disabled={isSaving || !selectedTrainer}
									className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{t('projects.details.trainerDialog.choose')}
								</button>
					</div>
				</div>
			</div>
		</div>
	)
}
